import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight, Search } from "lucide-react";
import api, { eventImageUrl } from "@/lib/api";
import CountdownBanner from "@/components/CountdownBanner";
import Reveal from "@/components/Reveal";
import GoldParticles from "@/components/GoldParticles";
import { Dialog, DialogContent } from "@/components/ui/dialog";

const LEVELS = [
  { key: "all", label: "All Events" },
  { key: "Senior College", label: "Senior College" },
  { key: "Junior College", label: "Junior College" },
];

const SUITS = ["♠", "♦", "♣", "♥"];

const DETAIL_FIELDS = [
  ["registration_format", "Format"],
  ["team_size", "Team Size"],
  ["rounds", "Rounds"],
  ["prize", "Prize"],
  ["eligibility", "Eligibility"],
  ["rules", "Rules"],
  ["event_date", "Date"],
  ["event_time", "Time"],
  ["venue", "Venue"],
  ["registration_deadline", "Registration Deadline"],
];

const STATUS_STYLES = {
  Upcoming: "border-[#B39343]/50 text-[#B39343]",
  Closed: "border-[#E74C3C]/50 text-[#E74C3C]",
  Completed: "border-white/20 text-[#6E6B63]",
};

function StatusBadge({ status }) {
  if (!status || status === "Open") return null;
  return (
    <span className={`border px-2.5 py-1 text-[9px] uppercase tracking-[0.2em] ${STATUS_STYLES[status] || "border-white/20 text-[#A19E95]"}`}>
      {status}
    </span>
  );
}

function EventCard({ ev, index, onOpen }) {
  const img = eventImageUrl(ev);
  return (
    <Reveal delay={(index % 3) * 0.07} className="h-full">
      <button
        onClick={() => onOpen(ev)}
        data-testid={`event-card-${index}`}
        className={`group relative flex h-full w-full flex-col border bg-[#13131A] text-left transition-all duration-500 hover:border-[#D4AF37]/45 hover:bg-[#16161f] ${
          ev.featured ? "border-[#D4AF37]/40" : "border-white/8"
        }`}
      >
        {img && (
          <div className="relative aspect-[16/10] overflow-hidden">
            <img src={img} alt={ev.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#13131A] via-transparent to-transparent" />
          </div>
        )}
        <div className="flex flex-1 flex-col p-7">
          <div className="flex items-center justify-between gap-3">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#B39343]">{ev.college_level}</p>
            <span className="text-xl text-[#D4AF37]/15 transition-colors duration-500 group-hover:text-[#D4AF37]/35" aria-hidden="true">
              {SUITS[index % SUITS.length]}
            </span>
          </div>
          {ev.category && (
            <p className="mt-2.5 font-mono text-[10px] uppercase tracking-[0.25em] text-[#D4AF37]">{ev.category}</p>
          )}
          <h2 className="mt-3 font-display text-xl sm:text-2xl font-semibold text-[#F6F4EE]">{ev.name}</h2>
          {ev.short_description && (
            <p className="mt-3 flex-1 text-sm leading-relaxed text-[#A19E95] line-clamp-2">{ev.short_description}</p>
          )}
          <div className="mt-7 flex items-center justify-between border-t border-white/5 pt-5">
            <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#E5C97A] transition-colors group-hover:text-[#D4AF37]">
              View Details
              <ArrowUpRight size={13} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </span>
            <StatusBadge status={ev.status} />
          </div>
        </div>
        {ev.featured && (
          <span className="absolute left-0 top-0 bg-[#D4AF37] px-3 py-1 text-[9px] font-bold uppercase tracking-[0.2em] text-[#08080A]">
            Featured
          </span>
        )}
      </button>
    </Reveal>
  );
}

function EventModal({ ev, onClose }) {
  const navigate = useNavigate();
  if (!ev) return null;
  const img = eventImageUrl(ev);
  const filled = DETAIL_FIELDS.filter(([key]) => ev[key] && String(ev[key]).trim());
  const closed = ev.status === "Closed" || ev.status === "Completed";

  return (
    <Dialog open={!!ev} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="border-[#D4AF37]/25 bg-[#0F0F14] sm:max-w-2xl max-h-[88vh] overflow-y-auto p-0" data-testid="event-detail-modal">
        {img && (
          <div className="relative aspect-[21/9] w-full overflow-hidden">
            <img src={img} alt={ev.name} className="h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0F0F14] via-transparent to-transparent" />
          </div>
        )}
        <div className="p-8 sm:p-10">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#B39343]">{ev.college_level}</p>
            {ev.category && <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#D4AF37]">{ev.category}</p>}
            <StatusBadge status={ev.status} />
          </div>
          <h2 className="mt-4 font-display text-2xl sm:text-4xl font-bold text-[#F6F4EE]">{ev.name}</h2>
          {(ev.full_description || ev.short_description) && (
            <p className="mt-5 text-sm sm:text-base leading-relaxed text-[#A19E95]">
              {ev.full_description || ev.short_description}
            </p>
          )}
          {filled.length > 0 && (
            <dl className="mt-8 grid gap-x-8 gap-y-5 sm:grid-cols-2 border-t border-white/8 pt-7">
              {filled.map(([key, label]) => (
                <div key={key}>
                  <dt className="label-lux mb-1">{label}</dt>
                  <dd className="text-sm leading-relaxed text-[#F6F4EE]/90 whitespace-pre-line">{ev[key]}</dd>
                </div>
              ))}
            </dl>
          )}
          {closed ? (
            <p data-testid="event-modal-closed" className="mt-9 border border-[#E74C3C]/30 bg-[#E74C3C]/5 px-6 py-4 text-center text-[11px] uppercase tracking-[0.22em] text-[#E74C3C]">
              Registration {ev.status === "Completed" ? "Completed" : "Closed"}
            </p>
          ) : (
            <button
              data-testid="event-modal-register-btn"
              onClick={() => navigate(`/register?event=${encodeURIComponent(ev.name)}`)}
              className="group mt-9 inline-flex w-full items-center justify-center gap-3 bg-[#D4AF37] px-8 py-4 text-xs font-semibold uppercase tracking-[0.22em] text-[#08080A] transition-colors duration-300 hover:bg-[#E5C97A]"
            >
              Register Now
              <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Events() {
  const [events, setEvents] = useState([]);
  const [level, setLevel] = useState("all");
  const [category, setCategory] = useState("all");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get("/events").then((r) => setEvents(r.data)).catch(() => {});
  }, []);

  const categories = useMemo(
    () => [...new Set(events.map((e) => e.category).filter(Boolean))],
    [events]
  );

  const visible = events.filter((ev) => {
    if (level !== "all" && ev.college_level !== level) return false;
    if (category !== "all" && ev.category !== category) return false;
    if (search.trim()) {
      const needle = search.trim().toLowerCase();
      const hay = `${ev.name} ${ev.category} ${ev.short_description}`.toLowerCase();
      if (!hay.includes(needle)) return false;
    }
    return true;
  });

  return (
    <div data-testid="events-page">
      <CountdownBanner />
      <section className="relative overflow-hidden border-b border-white/5 py-24 sm:py-28">
        <GoldParticles density={30} />
        <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <p className="eyebrow mb-5">The Events</p>
            <h1 data-testid="events-heading" className="max-w-3xl font-display text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight text-[#F6F4EE]">
              Ten Events.<br />
              <span className="text-gold-sheen">Pick Your Challenge.</span>
            </h1>
            <p className="mt-7 max-w-2xl text-sm sm:text-base leading-relaxed text-[#A19E95]">
              Eight senior college challenges and two junior college events — from trading floors and forensic
              ledgers to the cultural stage. Study the brief, then register.
            </p>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="mt-10 flex flex-wrap items-center gap-3" data-testid="events-level-filters">
              {LEVELS.map((l) => (
                <button
                  key={l.key}
                  data-testid={`events-filter-${l.key === "all" ? "all" : l.key === "Senior College" ? "senior" : "junior"}`}
                  onClick={() => setLevel(l.key)}
                  className={`border px-5 py-2.5 text-[11px] uppercase tracking-[0.2em] transition-all duration-300 ${
                    level === l.key
                      ? "border-[#D4AF37] bg-[#D4AF37] text-[#08080A]"
                      : "border-white/15 text-[#A19E95] hover:border-[#D4AF37]/50 hover:text-[#F6F4EE]"
                  }`}
                >
                  {l.label}
                </button>
              ))}
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <div className="relative">
                <Search size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#6E6B63]" />
                <input
                  data-testid="events-search"
                  className="input-lux w-full sm:w-64 pl-10"
                  placeholder="Search events..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <select
                data-testid="events-category-filter"
                className="input-lux w-full sm:w-56"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
              >
                <option value="all">All Categories</option>
                {categories.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-20 sm:py-24">
        {visible.length === 0 ? (
          <p className="py-16 text-center text-sm text-[#6E6B63]" data-testid="events-empty">No events match this selection.</p>
        ) : (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="events-grid">
            {visible.map((ev, i) => (
              <EventCard key={ev.id} ev={ev} index={i} onOpen={setSelected} />
            ))}
          </div>
        )}
      </section>

      <EventModal ev={selected} onClose={() => setSelected(null)} />
    </div>
  );
}
